<template>
  <v-dialog v-model="dialog" max-width="700" scrollable>
    <v-card class="support-card">
      <!-- Header -->
      <div class="support-header">
        <v-avatar size="52" class="flex-shrink-0">
          <v-img :src="logo" alt="Ordera" />
        </v-avatar>
        <div class="support-header__text">
          <p class="support-header__title mb-0">{{ $t("help_and_support") }}</p>
          <p class="support-header__subtitle mb-0">
            {{ $t("help_and_support_subtitle") }}
          </p>
        </div>
        <v-btn icon class="support-header__close flex-shrink-0" @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="support-body">
        <!-- Clear cache / soft update -->
        <div class="cache-box">
          <v-icon color="primary" size="30" class="flex-shrink-0 mt-1">mdi-broom</v-icon>
          <div class="cache-box__text">
            <p class="cache-box__title mb-1">{{ $t("clear_app_cache") }}</p>
            <p class="cache-box__desc mb-0">{{ $t("clear_app_cache_desc") }}</p>
          </div>
          <v-btn
            color="primary"
            depressed
            class="cache-box__btn rounded-lg flex-shrink-0"
            :loading="clearing"
            :disabled="clearing || resetting"
            @click="clearCache"
          >
            <v-icon left small>mdi-broom</v-icon>
            {{ $t("clear_cache") }}
          </v-btn>
        </div>

        <!-- Contact card -->
        <div class="contact-card">
          <div class="contact-card__head">
            <v-avatar size="40" color="white" class="contact-card__icon flex-shrink-0">
              <v-icon color="grey darken-1">mdi-message-text-outline</v-icon>
            </v-avatar>
            <div class="min-w-0">
              <p class="support-row__title mb-0">{{ $t("contact_and_support") }}</p>
              <p class="support-row__desc mb-0">{{ $t("contact_and_support_desc") }}</p>
            </div>
          </div>
          <div class="contact-card__cols">
            <div class="contact-col">
              <v-icon color="primary" class="flex-shrink-0">mdi-email-outline</v-icon>
              <div class="min-w-0">
                <a href="mailto:help@ordera.app" class="support-link support-link--lg">
                  help@ordera.app
                </a>
                <p class="support-row__desc mb-0">{{ $t("email_reply_time") }}</p>
              </div>
            </div>
            <v-divider vertical class="contact-card__divider" />
            <div class="contact-col">
              <v-icon color="#7360F2" class="flex-shrink-0">mdi-phone</v-icon>
              <div class="min-w-0">
                <a href="tel:+38765021012" class="support-link support-link--lg d-block">
                  +387 65 021 012
                </a>
                <a href="tel:+38766516174" class="support-link support-link--lg d-block">
                  +387 66 516 174
                </a>
                <p class="support-row__desc mb-0">{{ $t("viber_availability") }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Useful links (collapsible) -->
        <v-expansion-panels flat :value="0" class="useful-links">
          <v-expansion-panel>
            <v-expansion-panel-header class="useful-links__header">
              <div class="d-flex align-center">
                <v-avatar
                  size="40"
                  color="white"
                  class="useful-links__icon flex-shrink-0 mr-3"
                >
                  <v-icon color="grey darken-1">mdi-link-variant</v-icon>
                </v-avatar>
                <div class="min-w-0">
                  <p class="support-row__title mb-0">{{ $t("useful_links") }}</p>
                  <p class="support-row__desc mb-0">{{ $t("useful_links_desc") }}</p>
                </div>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="useful-links__content">
              <div v-for="(row, i) in rows" :key="i" class="link-row">
                <v-icon color="grey darken-1" size="22" class="flex-shrink-0">
                  {{ row.icon }}
                </v-icon>
                <div class="link-row__text">
                  <p class="link-row__title mb-0">{{ row.title }}</p>
                  <p class="support-row__desc mb-0">{{ row.desc }}</p>
                </div>
                <a
                  :href="row.href"
                  target="_blank"
                  rel="noopener"
                  class="support-link link-row__link"
                >
                  {{ row.linkText }}
                  <v-icon x-small color="primary">mdi-open-in-new</v-icon>
                </a>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-divider class="mt-4 mb-3" />

        <!-- Footer -->
        <div class="support-footer">
          <v-icon class="mr-2 flex-shrink-0" color="grey darken-1">
            mdi-information-outline
          </v-icon>
          <div class="flex-grow-1 min-w-0">
            <p class="support-row__title mb-0">{{ $t("other_problem") }}</p>
            <p class="support-row__desc mb-0">{{ $t("other_problem_desc") }}</p>
          </div>
          <v-btn
            outlined
            small
            color="grey darken-1"
            class="flex-shrink-0 text-none rounded-lg"
            href="https://help.ordera.app"
            target="_blank"
            rel="noopener"
          >
            {{ $t("open_help_center") }}
            <v-icon right x-small>mdi-open-in-new</v-icon>
          </v-btn>
        </div>

        <div class="support-meta">
          <v-btn
            text
            x-small
            class="grey--text"
            :disabled="clearing || resetting"
            @click="resetApp"
          >
            {{ $t("reset_application") }}
          </v-btn>
          <span class="support-version">{{ version }}</span>
        </div>
      </v-card-text>
    </v-card>

    <global-confirm-box ref="confirmBox" />
  </v-dialog>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import {
  getAppVersion,
  updateApplication,
  resetApplication,
} from "@/utils/appMaintenance";
import logo from "@/assets/logo.png";

export default {
  name: "SupportInfoDialog",
  mixins: [hasSnackbar],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    logo,
    clearing: false,
    resetting: false,
  }),
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    version() {
      return getAppVersion() || "—";
    },
    rows() {
      return [
        {
          icon: "mdi-cellphone",
          title: this.$t("mobile_app"),
          desc: this.$t("mobile_app_desc"),
          linkText: "ordera.app/mobile",
          href: "https://ordera.app/mobile",
        },
        {
          icon: "mdi-truck-fast-outline",
          title: this.$t("couriers_dispatchers_app"),
          desc: this.$t("couriers_dispatchers_app_desc"),
          linkText: "kur.ordera.app",
          href: "https://kur.ordera.app",
        },
        {
          icon: "mdi-domain",
          title: this.$t("corporate_nutrition"),
          desc: this.$t("corporate_nutrition_desc"),
          linkText: "topli-obrok.ordera.app",
          href: "https://topli-obrok.ordera.app",
        },
        {
          icon: "mdi-room-service-outline",
          title: this.$t("order_from_home"),
          desc: this.$t("order_from_home_desc"),
          linkText: "ordera.app",
          href: "https://ordera.app",
        },
        {
          icon: "mdi-silverware-fork-knife",
          title: this.$t("digital_menu"),
          desc: this.$t("digital_menu_desc"),
          linkText: "konobar.ordera.app/info",
          href: "https://konobar.ordera.app/info",
        },
        {
          icon: "mdi-handshake-outline",
          title: this.$t("become_ordera_partner"),
          desc: this.$t("become_ordera_partner_desc"),
          linkText: "partner.ordera.app",
          href: "https://partner.ordera.app",
        },
      ];
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
      this.$emit("close");
    },
    async clearCache() {
      this.clearing = true;
      this.showInfoSnackbar(this.$t("updating_application"));
      // Soft update: refresh the Service Worker and drop Cache Storage, then
      // reload. Keeps localStorage / sessionStorage / IndexedDB, so the user
      // stays logged in. Ends in window.location.reload().
      await updateApplication();
    },
    async resetApp() {
      try {
        await this.$refs.confirmBox.open(
          this.$t("reset_application_warning_title"),
          this.$t("reset_application_warning"),
          { color: "error", zIndex: 400 }
        );
      } catch (cancelled) {
        return;
      }
      this.resetting = true;
      await resetApplication();
    },
  },
};
</script>

<style scoped>
.support-card {
  border-radius: 20px;
}

/* Header */
.support-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px 14px;
}

.support-header__text {
  flex: 1 1 auto;
  min-width: 0;
}

.support-header__title {
  font-size: 1.45rem;
  font-weight: 700;
  line-height: 1.2;
  color: #1f2d3d;
}

.support-header__subtitle {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.55);
  margin-top: 2px;
}

.support-header__close.v-btn--icon {
  background: #f0f0f2;
}

.support-body {
  padding: 4px 24px 24px !important;
}

.min-w-0 {
  min-width: 0;
}

/* Clear cache box */
.cache-box {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: rgba(251, 109, 59, 0.1);
  border-radius: 16px;
  padding: 16px 20px;
  margin-bottom: 14px;
}

.cache-box__text {
  flex: 1 1 auto;
  min-width: 0;
}

.cache-box__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: rgb(251, 109, 59);
}

.cache-box__desc {
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.55);
  line-height: 1.45;
}

.cache-box__btn {
  align-self: center;
}

/* Contact card + useful links share the grey surface */
.contact-card,
.useful-links {
  background: #f5f5f6;
  border-radius: 16px;
  margin-bottom: 14px;
}

.contact-card {
  padding: 16px 20px;
}

.contact-card__head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.contact-card__icon {
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.contact-card__cols {
  display: flex;
  align-items: stretch;
  gap: 20px;
}

.contact-col {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 1 1 0;
  min-width: 0;
}

.contact-card__divider {
  margin: 2px 0;
}

/* Shared text styles */
.support-row__title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #1f2d3d;
}

.support-row__desc {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
  line-height: 1.4;
}

.support-link {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  text-decoration: none;
  color: rgb(251, 109, 59);
  font-size: 0.8rem;
  font-weight: 600;
  word-break: break-word;
}

.support-link--lg {
  font-size: 0.95rem;
  line-height: 1.5;
}

/* Useful links panel */
.useful-links >>> .v-expansion-panel,
.useful-links >>> .v-expansion-panel::before {
  background: transparent !important;
  box-shadow: none !important;
}

.useful-links >>> .v-expansion-panel-header {
  padding: 16px 20px;
  min-height: unset;
}

.useful-links >>> .v-expansion-panel-content__wrap {
  padding: 0 12px 12px;
}

.link-row {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 8px;
}

.link-row:last-child {
  margin-bottom: 0;
}

.link-row__text {
  flex: 1 1 auto;
  min-width: 0;
}

.link-row__title {
  font-size: 0.82rem;
  font-weight: 700;
  color: #1f2d3d;
}

.link-row__link {
  flex-shrink: 0;
  white-space: nowrap;
}

/* Footer */
.support-footer {
  display: flex;
  align-items: center;
  gap: 8px;
}

.support-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
}

.support-version {
  font-size: 0.7rem;
  color: rgba(0, 0, 0, 0.35);
}

@media (max-width: 600px) {
  .support-header__title {
    font-size: 1.2rem;
  }

  .cache-box {
    flex-wrap: wrap;
  }

  .cache-box__btn {
    width: 100%;
    margin-top: 8px;
  }

  .contact-card__cols {
    flex-direction: column;
    gap: 12px;
  }

  .contact-card__divider {
    display: none;
  }

  .link-row {
    flex-wrap: wrap;
  }

  .link-row__link {
    padding-left: 34px;
    white-space: normal;
  }
}
</style>
